import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart3, Download, FileSpreadsheet, FileText, Users, Layers, ShieldAlert, Building2, Upload, CheckCircle2, XCircle, ChevronDown, ChevronUp, Save, MessageSquare, Link2, Edit2, Loader2, Search, Check, UserCheck, Filter, X, Calendar, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ZohoCustomerLink, ZohoInvoiceLink } from '../../components/ZohoLinks';
import { BookmarkInline, BookmarkMapProvider, useBookmarkMap, bookmarkRowClass } from '../../components/BookmarkButton';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TABS = [
  { id: 'trainer', label: 'Trainer Report', icon: Users },
  { id: 'batch', label: 'Batch Report', icon: Layers },
  { id: 'attendance', label: 'Attendance & Placement', icon: UserCheck },
  { id: 'overdue', label: 'Overdue Report', icon: ShieldAlert },
  { id: 'partially_paid', label: 'Partially Paid', icon: FileText },
  { id: 'upgrade', label: 'Upgrade/Revision', icon: Upload },
  { id: 'operational', label: 'Operational Report', icon: Building2 },
];

function exportToExcel(rows, columns, filename) {
  const data = rows.map(r => {
    const obj = {};
    columns.forEach(c => { obj[c.label] = c.render ? c.render(r) : r[c.key]; });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

function exportToPdf(rows, columns, title) {
  const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(14);
  doc.text(title, 14, 18);
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 24);
  autoTable(doc, {
    startY: 28,
    head: [columns.map(c => c.label)],
    body: rows.map(r => columns.map(c => c.render ? c.render(r) : (r[c.key] ?? '-'))),
    styles: { fontSize: 7 },
    headStyles: { fillColor: [109, 40, 217] },
  });
  doc.save(`${title.replace(/\s/g, '_')}.pdf`);
}

const fmt = v => v != null ? `₹${Number(v).toLocaleString('en-IN')}` : '-';

// ─── TRAINER REPORT (REVAMPED) ───────────────────────────────

function EditableCell({ value, field, batchId, studentId, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const ref = useRef(null);

  useEffect(() => { setVal(value || ''); }, [value]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const save = async () => {
    setEditing(false);
    if (val !== (value || '')) {
      await onSave(batchId, studentId, { [field]: val });
    }
  };

  if (!editing) {
    return (
      <div className="cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded min-h-[24px] text-xs text-slate-600 group flex items-center gap-1"
        onClick={() => setEditing(true)} title="Click to edit">
        {val || <span className="text-slate-300 italic">-</span>}
        <Edit2 className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100" />
      </div>
    );
  }
  return (
    <Input ref={ref} value={val} onChange={e => setVal(e.target.value)}
      onBlur={save} onKeyDown={e => e.key === 'Enter' && save()}
      className="h-6 text-xs px-1 w-full min-w-[60px]" />
  );
}

function AttendanceCell({ value, batchId, studentId, onSave }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const handleChange = async (newVal) => {
    setVal(newVal);
    await onSave(batchId, studentId, { attendance: newVal });
  };
  const colors = {
    Regular: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Absenteeism: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <Select value={val || '__none__'} onValueChange={v => handleChange(v === '__none__' ? '' : v)}>
      <SelectTrigger className={`h-6 text-[10px] rounded-sm min-w-[90px] border ${colors[val] || 'border-slate-200'}`} data-testid={`attendance-${studentId}`}>
        <SelectValue placeholder="-" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">-</SelectItem>
        <SelectItem value="Regular">Regular</SelectItem>
        <SelectItem value="Absenteeism">Absenteeism</SelectItem>
      </SelectContent>
    </Select>
  );
}

function PlacementCheckbox({ value, batchId, studentId, onSave }) {
  const [checked, setChecked] = useState(!!value);
  useEffect(() => { setChecked(!!value); }, [value]);
  const handleToggle = async () => {
    const newVal = !checked;
    setChecked(newVal);
    await onSave(batchId, studentId, { ready_for_placement: newVal });
  };
  return (
    <button
      onClick={handleToggle}
      className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${
        checked ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300 hover:border-slate-400'
      }`}
      data-testid={`placement-check-${studentId}`}
      title={checked ? 'Ready for placement' : 'Not ready'}
    >
      {checked && <Check className="w-3.5 h-3.5 text-white" />}
    </button>
  );
}

function BatchCard({ batch, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [students, setStudents] = useState([]);
  const [batchComment, setBatchComment] = useState(batch.comment || '');
  const [whatsappLink, setWhatsappLink] = useState(batch.whatsapp_link || '');
  const [loading, setLoading] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const commentChanged = batchComment !== (batch.comment || '') || whatsappLink !== (batch.whatsapp_link || '');

  const loadStudents = useCallback(async () => {
    if (students.length > 0) return;
    setLoading(true);
    try {
      const r = await api.get(`/training/reports/trainer-detail/${batch.batch_id}/students`);
      setStudents(r.data.students || []);
      const bc = r.data.batch_comment || {};
      setBatchComment(bc.comment || '');
      setWhatsappLink(bc.whatsapp_link || '');
    } catch { toast.error('Failed to load students'); }
    setLoading(false);
  }, [batch.batch_id, students.length]);

  const handleExpand = () => {
    if (!expanded) loadStudents();
    setExpanded(!expanded);
  };

  const handleSaveComment = async () => {
    setSavingComment(true);
    try {
      await api.put(`/training/reports/batch-comments/${batch.batch_id}`, { comment: batchComment, whatsapp_link: whatsappLink });
      toast.success('Batch comment saved');
    } catch { toast.error('Failed to save'); }
    setSavingComment(false);
  };

  const handleSaveRemark = async (batchId, studentId, data) => {
    try {
      await api.put(`/training/reports/batch-remarks/${batchId}/student/${studentId}`, data);
    } catch { toast.error('Failed to save remark'); }
  };

  const handleUpdateStudent = (studentId, field, newVal) => {
    setStudents(prev => prev.map(s => s.student_id === studentId ? { ...s, [field]: newVal } : s));
  };

  const saveAndUpdate = async (batchId, studentId, data) => {
    await handleSaveRemark(batchId, studentId, data);
    const field = Object.keys(data)[0];
    handleUpdateStudent(studentId, field, data[field]);
  };

  return (
    <Card className="border overflow-hidden">
      {/* Batch Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50/70 cursor-pointer hover:bg-slate-100/70 transition-colors"
        onClick={handleExpand} data-testid={`batch-card-${batch.batch_id}`}>
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold text-slate-800">{batch.name || batch.course_name}</div>
          {batch.zoho_batch_id && <Badge variant="outline" className="text-[9px] bg-cyan-50 text-cyan-700 border-cyan-200">CRM #{batch.zoho_batch_id}</Badge>}
          {batch.batch_number && <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">LMS #{batch.batch_number}</Badge>}
          <span className="text-[10px] text-slate-400">{batch.timing}</span>
          <span className="text-[10px] text-slate-400">{batch.start_date} → {batch.end_date || '...'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[9px]">{batch.student_count} students</Badge>
          <Badge variant="outline" className={`text-[9px] ${batch.status === 'Running' ? 'bg-emerald-50 text-emerald-700' : batch.status === 'Completed' ? 'bg-blue-50 text-blue-700' : batch.status === 'On Hold' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
            {batch.status}
          </Badge>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>
          ) : (
            <>
              {/* Student Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-cyan-50/50">
                      <TableHead className="text-[9px] uppercase w-8">S.No</TableHead>
                      <TableHead className="text-[9px] uppercase">Enroll No</TableHead>
                      <TableHead className="text-[9px] uppercase">Name</TableHead>
                      <TableHead className="text-[9px] uppercase">Phone</TableHead>
                      <TableHead className="text-[9px] uppercase">Email</TableHead>
                      <TableHead className="text-[9px] uppercase">Centre</TableHead>
                      <TableHead className="text-[9px] uppercase">Counsellor</TableHead>
                      <TableHead className="text-[9px] uppercase">Sector</TableHead>
                      <TableHead className="text-[9px] uppercase">PDP</TableHead>
                      <TableHead className="text-[9px] uppercase">Mock Int.</TableHead>
                      <TableHead className="text-[9px] uppercase">Attendance</TableHead>
                      <TableHead className="text-[9px] uppercase">Ready for Placement</TableHead>
                      <TableHead className="text-[9px] uppercase text-right">Dues Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow><TableCell colSpan={13} className="text-center text-xs text-slate-400 py-6">No students enrolled</TableCell></TableRow>
                    ) : students.map(s => (
                      <TableRow key={s.student_id} className="hover:bg-slate-50/50">
                        <TableCell className="text-xs text-slate-400">{s.sno}</TableCell>
                        <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} /></TableCell>
                        <TableCell className="text-xs font-medium text-slate-800">{s.name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{s.phone}</TableCell>
                        <TableCell className="text-[10px] text-blue-600 max-w-[120px] truncate">{s.email}</TableCell>
                        <TableCell className="text-[10px] text-slate-500">{s.centre || '-'}</TableCell>
                        <TableCell><EditableCell value={s.counsellor} field="counsellor" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                        <TableCell><EditableCell value={s.sector} field="sector" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                        <TableCell><EditableCell value={s.pdp} field="pdp" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                        <TableCell><EditableCell value={s.mock_interview} field="mock_interview" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                        <TableCell>
                          <AttendanceCell value={s.attendance} batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} />
                        </TableCell>
                        <TableCell className="text-center">
                          <PlacementCheckbox value={s.ready_for_placement} batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} />
                        </TableCell>
                        <TableCell className="text-right">
                          {s.has_overdue ? (
                            <Badge variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-200">Overdue</Badge>
                          ) : s.has_partially_paid ? (
                            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">Partially Paid</Badge>
                          ) : (
                            <span className="text-[10px] text-slate-300">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Batch Comment & WhatsApp */}
              <div className="border-t p-4 bg-slate-50/30 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Batch Overall Comment</Label>
                    <Textarea value={batchComment} onChange={e => setBatchComment(e.target.value)}
                      className="mt-1 text-xs min-h-[60px]" placeholder="Overall batch comments..." />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1"><Link2 className="w-3 h-3" /> WhatsApp Group Link</Label>
                    <Input value={whatsappLink} onChange={e => setWhatsappLink(e.target.value)}
                      className="mt-1 text-xs h-8" placeholder="https://chat.whatsapp.com/..." />
                  </div>
                </div>
                {commentChanged && (
                  <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700 rounded-sm"
                    onClick={handleSaveComment} disabled={savingComment}>
                    <Save className="w-3 h-3 mr-1" />{savingComment ? 'Saving...' : 'Save Batch Info'}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function TrainerSearch({ trainers, selectedTrainer, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const selected = trainers.find(t => t.id === selectedTrainer);
  const filtered = query
    ? trainers.filter(t => t.name.toLowerCase().includes(query.toLowerCase()))
    : trainers;

  return (
    <div className="relative w-72" data-testid="trainer-search">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <Input
          ref={inputRef}
          placeholder="Search trainer..."
          value={open ? query : (selected ? selected.name : query)}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          className="h-9 pl-8 text-sm"
          data-testid="trainer-search-input"
        />
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No trainers found</div>
            ) : filtered.map(t => (
              <div key={t.id}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-violet-50 transition-colors ${t.id === selectedTrainer ? 'bg-violet-50 text-violet-700 font-medium' : 'text-slate-700'}`}
                onClick={() => { onSelect(t.id); setQuery(''); setOpen(false); }}
                data-testid={`trainer-option-${t.id}`}
              >
                {t.name} {t.status !== 'active' ? <span className="text-[10px] text-slate-400 ml-1">(inactive)</span> : ''}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TrainerReport() {
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [data, setData] = useState({ weekday_running: [], weekend_running: [], completed: [] });
  const [loading, setLoading] = useState(false);
  const [subTab, setSubTab] = useState('weekday');

  useEffect(() => {
    api.get('/training/reports/trainer-detail').then(r => setTrainers(r.data.trainers || []));
  }, []);

  const loadTrainer = useCallback(async (tid) => {
    if (!tid) return;
    setLoading(true);
    try {
      const r = await api.get(`/training/reports/trainer-detail?trainer_id=${tid}`);
      setData({
        weekday_running: r.data.weekday_running || [],
        weekend_running: r.data.weekend_running || [],
        completed: r.data.completed || [],
      });
    } catch { toast.error('Failed to load trainer report'); }
    setLoading(false);
  }, []);

  useEffect(() => { if (selectedTrainer) loadTrainer(selectedTrainer); }, [selectedTrainer, loadTrainer]);

  const subTabs = [
    { id: 'weekday', label: 'Weekday', count: data.weekday_running.length },
    { id: 'weekend', label: 'Weekend', count: data.weekend_running.length },
    { id: 'completed', label: 'Completed Batches', count: data.completed.length },
  ];

  const currentBatches = subTab === 'weekday' ? data.weekday_running
    : subTab === 'weekend' ? data.weekend_running
    : data.completed;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <TrainerSearch trainers={trainers} selectedTrainer={selectedTrainer}
          onSelect={tid => { setSelectedTrainer(tid); setSubTab('weekday'); }} />
      </div>

      {selectedTrainer && (
        <>
          {/* Sub-tabs: Weekday | Weekend | Completed */}
          <div className="border-b flex gap-0">
            {subTabs.map(st => (
              <button key={st.id} onClick={() => setSubTab(st.id)}
                className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${subTab === st.id ? 'border-cyan-500 text-cyan-700 bg-cyan-50/50' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                data-testid={`trainer-subtab-${st.id}`}>
                {st.label} <span className="ml-1 text-[10px] text-slate-400">({st.count})</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
          ) : currentBatches.length === 0 ? (
            <div className="text-center py-12"><p className="text-xs text-slate-400">No batches in this category</p></div>
          ) : (
            <div className="space-y-3">
              {currentBatches.map(b => <BatchCard key={b.batch_id} batch={b} />)}
            </div>
          )}
        </>
      )}

      {!selectedTrainer && (
        <div className="text-center py-16">
          <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Select a trainer to view their batch report</p>
          <p className="text-xs text-slate-300 mt-1">Batches will be split into Weekday, Weekend, and Completed tabs</p>
        </div>
      )}
    </div>
  );
}

// ─── BATCH REPORT ────────────────────────────────────────────
function BatchReport({ initialStatus = '' }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [centres, setCentres] = useState([]);
  const [filters, setFilters] = useState({ course_id: '', centre_id: '', status: initialStatus, batch_type: '', start_date: '', end_date: '' });

  const fetch = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    api.get(`/training/reports/batch?${params}`).then(r => { setRows(r.data.rows || []); setSummary(r.data.summary || {}); }).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => {
    Promise.all([api.get('/training/courses'), api.get('/training/centres')]).then(([cR, ceR]) => { setCourses(cR.data.courses || []); setCentres(ceR.data.centres || []); });
  }, []);

  const columns = [
    { key: 'course_name', label: 'Course' },
    { key: 'centre_name', label: 'Centre' },
    { key: 'batch_type', label: 'Type' },
    { key: 'start_date', label: 'Start' },
    { key: 'end_date', label: 'End' },
    { key: 'trainer_count', label: 'Trainers' },
    { key: 'student_count', label: 'Students' },
    { key: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-4">
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[{ l: 'Total', v: summary.total, c: 'text-slate-900' }, { l: 'Active', v: summary.active, c: 'text-emerald-600' }, { l: 'Completed', v: summary.completed, c: 'text-blue-600' }, { l: 'Cancelled', v: summary.cancelled, c: 'text-slate-400' }, { l: 'Delayed', v: summary.delayed, c: 'text-red-600' }, { l: 'Weekday', v: summary.weekday, c: 'text-violet-600' }, { l: 'Weekend', v: summary.weekend, c: 'text-amber-600' }].map(s => (
            <Card key={s.l} className="border p-3 text-center"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{s.l}</p><p className={`text-xl font-bold ${s.c}`}>{s.v || 0}</p></Card>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filters.course_id || 'all'} onValueChange={v => setFilters(p => ({ ...p, course_id: v === 'all' ? '' : v }))}><SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="All Courses" /></SelectTrigger><SelectContent><SelectItem value="all">All Courses</SelectItem>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Select value={filters.centre_id || 'all'} onValueChange={v => setFilters(p => ({ ...p, centre_id: v === 'all' ? '' : v }))}><SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="All Centres" /></SelectTrigger><SelectContent><SelectItem value="all">All Centres</SelectItem>{centres.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Select value={filters.status || 'all'} onValueChange={v => setFilters(p => ({ ...p, status: v === 'all' ? '' : v }))}><SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="All Statuses" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Running">Running</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="On Hold">On Hold</SelectItem><SelectItem value="Upcoming">Upcoming</SelectItem><SelectItem value="Cancelled">Cancelled</SelectItem></SelectContent></Select>
        <Select value={filters.batch_type || 'all'} onValueChange={v => setFilters(p => ({ ...p, batch_type: v === 'all' ? '' : v }))}><SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="All Types" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="weekday">Weekday</SelectItem><SelectItem value="weekend">Weekend</SelectItem></SelectContent></Select>
        <Input type="date" value={filters.start_date} onChange={e => setFilters(p => ({ ...p, start_date: e.target.value }))} className="h-8 text-xs w-36" />
        <Input type="date" value={filters.end_date} onChange={e => setFilters(p => ({ ...p, end_date: e.target.value }))} className="h-8 text-xs w-36" />
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToExcel(rows, columns, 'Batch_Report')}><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToPdf(rows, columns, 'Batch Report')}><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
      </div>
      <Card className="border p-0">
        {loading ? <div className="h-32 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div> : rows.length === 0 ? <p className="text-xs text-slate-400 text-center py-8">No data</p> : (
          <Table>
            <TableHeader><TableRow>{columns.map(c => <TableHead key={c.key} className="text-[10px] uppercase">{c.label}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.batch_id} className={r.is_delayed ? 'bg-red-50/50' : ''}>
                  <TableCell className="text-sm font-medium">{r.course_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.centre_name}</TableCell>
                  <TableCell className="text-xs capitalize">{r.batch_type}</TableCell>
                  <TableCell className="text-xs">{r.start_date}</TableCell>
                  <TableCell className="text-xs">{r.end_date}</TableCell>
                  <TableCell className="text-xs">{r.trainer_count}</TableCell>
                  <TableCell className="text-xs">{r.student_count}</TableCell>
                  <TableCell><div className="flex gap-1"><Badge variant="outline" className={`text-[10px] ${r.status === 'Running' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.status === 'Completed' ? 'bg-blue-50 text-blue-700 border-blue-200' : r.status === 'On Hold' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-500'}`}>{r.status}</Badge>{r.is_delayed && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">Delayed</Badge>}</div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ─── OVERDUE ROW (EXPANDABLE DRILL-DOWN) ────────────────────
function OverdueRow({ row: r }) {
  const [expanded, setExpanded] = useState(false);
  const { map } = useBookmarkMap();
  const bmHighlight = bookmarkRowClass(r.enrollment_no, map);

  return (
    <>
      <TableRow className={`cursor-pointer hover:bg-red-50/30 ${bmHighlight}`} onClick={() => setExpanded(!expanded)} data-testid={`overdue-row-${r.enrollment_no}`}>
        <TableCell className="w-6 px-2">
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </TableCell>
        <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={r.enrollment_no} zohoCustomerId={r.zoho_customer_id} /></TableCell>
        <TableCell>
          <div className="flex items-center">
            <span className="text-sm font-medium">{r.name}</span>
            <BookmarkInline student={{ enrollment_no: r.enrollment_no, id: r.student_id, name: r.name, course: r.course, due_amount: r.due_amount }} />
          </div>
        </TableCell>
        <TableCell className="text-xs text-slate-500">{r.phone}</TableCell>
        <TableCell className="text-xs text-right">{fmt(r.total_amount)}</TableCell>
        <TableCell className="text-xs text-right text-emerald-600">{fmt(r.paid_amount)}</TableCell>
        <TableCell className="text-sm text-right font-semibold text-red-600">{fmt(r.due_amount)}</TableCell>
        <TableCell className="text-xs text-slate-500 max-w-[240px]">
          {(r.batches || []).map((b, i) => (
            <span key={i}>
              {b.batch_name || 'N/A'}
              {b.batch_number && <span className="text-violet-500 ml-0.5">#{b.batch_number}</span>}
              {b.course_name && <span className="text-slate-400"> ({b.course_name})</span>}
              {i < r.batches.length - 1 ? ', ' : ''}
            </span>
          ))}
          {(!r.batches || r.batches.length === 0) && <span className="text-slate-300">No batch</span>}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-slate-50/60 p-0">
            <div className="p-4 space-y-3">
              {/* Financial Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { l: 'Total Bill', v: fmt(r.total_amount), c: 'text-slate-800' },
                  { l: 'Paid', v: fmt(r.paid_amount), c: 'text-emerald-600' },
                  { l: 'Write Off', v: r.write_off_amount > 0 ? fmt(r.write_off_amount) : '-', c: r.write_off_amount > 0 ? 'text-amber-600' : 'text-slate-400' },
                  { l: 'Pending Dues', v: fmt(r.due_amount), c: 'text-red-600' },
                  { l: 'Email', v: r.email || '-', c: 'text-blue-600', isText: true },
                ].map(s => (
                  <div key={s.l} className="bg-white border rounded-sm p-2.5 text-center">
                    <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{s.l}</p>
                    <p className={`text-sm font-semibold ${s.c} ${s.isText ? 'text-xs truncate' : ''}`}>{s.v}</p>
                  </div>
                ))}
              </div>

              {/* Invoice Details */}
              {(r.invoices && r.invoices.length > 0) ? (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Invoice Details</p>
                  <div className="bg-white border rounded-sm overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50">
                          <TableHead className="text-[9px] uppercase">Invoice #</TableHead>
                          <TableHead className="text-[9px] uppercase">Date</TableHead>
                          <TableHead className="text-[9px] uppercase text-right">Total</TableHead>
                          <TableHead className="text-[9px] uppercase text-right">Paid</TableHead>
                          <TableHead className="text-[9px] uppercase text-right">Write Off</TableHead>
                          <TableHead className="text-[9px] uppercase text-right">Balance</TableHead>
                          <TableHead className="text-[9px] uppercase">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {r.invoices.map((inv, i) => (
                          <React.Fragment key={i}>
                            <TableRow className="border-b-0">
                              <TableCell className="text-xs font-medium">
                                <ZohoInvoiceLink invoiceNumber={inv.invoice_number || inv.invoice_id} invoiceId={inv.invoice_id} />
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">{inv.date || '-'}</TableCell>
                              <TableCell className="text-xs text-right">{fmt(inv.total)}</TableCell>
                              <TableCell className="text-xs text-right text-emerald-600">{fmt(inv.payment_made)}</TableCell>
                              <TableCell className="text-xs text-right">{inv.write_off_amount > 0 ? <span className="text-amber-600">{fmt(inv.write_off_amount)}</span> : <span className="text-slate-300">-</span>}</TableCell>
                              <TableCell className="text-xs text-right font-medium text-red-600">{fmt(inv.balance)}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className={`text-[9px] ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                  {inv.status || 'unknown'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                            {/* Line Items sub-rows */}
                            {inv.line_items && inv.line_items.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={7} className="pt-0 pb-2 px-4">
                                  <div className="bg-slate-50 rounded-sm border border-dashed border-slate-200 px-3 py-2">
                                    <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Line Items ({inv.line_items.length})</p>
                                    <div className="space-y-1">
                                      {inv.line_items.map((li, j) => (
                                        <div key={j} className="flex items-center justify-between text-[11px]">
                                          <span className="font-medium text-slate-700 flex-1 min-w-0 truncate">{li.name || '-'}</span>
                                          <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                                            <span className="text-slate-400">Qty: {li.quantity || 1}</span>
                                            <span className="text-slate-500">Rate: {fmt(li.rate)}</span>
                                            {li.discount ? <span className="text-amber-500">Disc: {li.discount}</span> : null}
                                            <span className="font-semibold text-slate-700 w-20 text-right">{fmt(li.item_total)}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No invoice data available</p>
              )}

              {/* Batch Details */}
              {r.batches && r.batches.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Batch Details</p>
                  <div className="flex flex-wrap gap-2">
                    {r.batches.map((b, i) => (
                      <div key={i} className="bg-white border rounded-sm px-3 py-2 text-xs">
                        <span className="font-medium">{b.batch_name}</span>
                        {b.batch_number && <Badge variant="outline" className="ml-1.5 text-[9px] bg-violet-50 text-violet-700 border-violet-200">#{b.batch_number}</Badge>}
                        {b.course_name && <span className="text-slate-400 ml-1.5">{b.course_name}</span>}
                        <Badge variant="outline" className={`ml-1.5 text-[9px] ${b.status === 'Running' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{b.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── OVERDUE REPORT (ENHANCED WITH LIVE DATA) ───────────────
function OverdueReport({ filterType = 'overdue' }) {
  const isOverdue = filterType === 'overdue';
  const [liveRows, setLiveRows] = useState([]);
  const [liveSummary, setLiveSummary] = useState({});
  const [liveLoading, setLiveLoading] = useState(true);

  // Live data
  useEffect(() => {
    setLiveLoading(true);
    api.get(`/training/reports/overdue-live?filter_type=${filterType}`).then(r => {
      setLiveRows(r.data.rows || []);
      setLiveSummary(r.data.summary || {});
    }).catch(() => {}).finally(() => setLiveLoading(false));
  }, [filterType]);

  const liveColumns = [
    { key: 'enrollment_no', label: 'Enroll No' },
    { key: 'name', label: 'Student' },
    { key: 'phone', label: 'Phone' },
    { key: 'total_amount', label: 'Total', render: r => fmt(r.total_amount) },
    { key: 'paid_amount', label: 'Paid', render: r => fmt(r.paid_amount) },
    { key: 'due_amount', label: 'Pending Due', render: r => fmt(r.due_amount) },
    { key: 'batches', label: 'Batch(es)', render: r => (r.batches || []).map(b => b.batch_name || b.batch_number).filter(Boolean).join(', ') || '-' },
  ];

  return (
    <div className="space-y-4">
      {!liveLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { l: isOverdue ? 'Overdue Students' : 'Partially Paid Students', v: liveSummary.total_students, c: isOverdue ? 'text-red-600' : 'text-amber-600' },
            { l: 'Total Pending', v: fmt(liveSummary.total_due_amount), c: 'text-red-600' },
            { l: 'In Batches', v: liveSummary.with_batches, c: 'text-emerald-600' },
            { l: 'No Batch', v: liveSummary.without_batches, c: 'text-amber-600' },
          ].map(s => (
            <Card key={s.l} className="border p-3 text-center">
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{s.l}</p>
              <p className={`text-lg font-bold ${s.c}`}>{s.v ?? 0}</p>
            </Card>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToExcel(liveRows, liveColumns, `${isOverdue ? 'Overdue' : 'Partially_Paid'}_Report`)} data-testid="export-overdue-live-excel"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToPdf(liveRows, liveColumns, `${isOverdue ? 'Overdue' : 'Partially Paid'} Report`)} data-testid="export-overdue-live-pdf"><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
      </div>
      <Card className="border p-0">
        {liveLoading ? <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div> : liveRows.length === 0 ? (
          <div className="text-center py-12"><ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-3" /><p className="text-xs text-slate-400">{isOverdue ? 'No students with overdue invoices.' : 'No students with partially paid invoices.'}</p></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase w-6"></TableHead>
                <TableHead className="text-[10px] uppercase">Enroll No</TableHead>
                <TableHead className="text-[10px] uppercase">Student</TableHead>
                <TableHead className="text-[10px] uppercase">Phone</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Paid</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Pending Due</TableHead>
                <TableHead className="text-[10px] uppercase">Batch(es)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {liveRows.map(r => (
                <OverdueRow key={r.student_id} row={r} />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ─── UPGRADE/REVISION REPORT ─────────────────────────────────
function UpgradeRevisionReport() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/training/reports/upgrade-revision')
      .then(r => { setRows(r.data.rows || []); setSummary(r.data.summary || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { key: 'enrollment_no', label: 'Enroll No' },
    { key: 'student_name', label: 'Student' },
    { key: 'phone', label: 'Phone' },
    { key: 'batch_name', label: 'Batch' },
    { key: 'batch_number', label: 'Batch #' },
    { key: 'batch_course', label: 'Course' },
    { key: 'trainer_name', label: 'Trainer' },
    { key: 'batch_status', label: 'Status' },
  ];

  const bsColors = {
    Running: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Completed: 'bg-blue-50 text-blue-700 border-blue-200',
    'On Hold': 'bg-amber-50 text-amber-700 border-amber-200',
    Upcoming: 'bg-violet-50 text-violet-700 border-violet-200',
  };

  return (
    <div className="space-y-4">
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card className="border p-3 text-center">
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Upgrade/Revision Students</p>
            <p className="text-lg font-bold text-purple-600">{summary.total_students ?? 0}</p>
          </Card>
          <Card className="border p-3 text-center">
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Across Batches</p>
            <p className="text-lg font-bold text-slate-800">{summary.total_batches ?? 0}</p>
          </Card>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToExcel(rows, columns, 'Upgrade_Revision_Report')}><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToPdf(rows, columns, 'Upgrade/Revision Students Report')}><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
      </div>
      <Card className="border p-0">
        {loading ? (
          <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12">
            <Upload className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-xs text-slate-400">No upgrade/revision students found. Run a CRM sync to populate this data.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase">Enroll No</TableHead>
                <TableHead className="text-[10px] uppercase">Student</TableHead>
                <TableHead className="text-[10px] uppercase">Phone</TableHead>
                <TableHead className="text-[10px] uppercase">Email</TableHead>
                <TableHead className="text-[10px] uppercase">Batch</TableHead>
                <TableHead className="text-[10px] uppercase">Batch #</TableHead>
                <TableHead className="text-[10px] uppercase">Course</TableHead>
                <TableHead className="text-[10px] uppercase">Trainer</TableHead>
                <TableHead className="text-[10px] uppercase">Centre</TableHead>
                <TableHead className="text-[10px] uppercase">Status</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Due</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.enrollment_id}>
                  <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={r.enrollment_no} zohoCustomerId={r.zoho_customer_id} /></TableCell>
                  <TableCell className="text-sm font-medium">{r.student_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.phone || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.email || '-'}</TableCell>
                  <TableCell className="text-xs font-medium">{r.batch_name || '-'}</TableCell>
                  <TableCell className="text-xs text-violet-500">#{r.batch_number}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.batch_course || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.trainer_name || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.centre_name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[9px] ${bsColors[r.batch_status] || 'bg-slate-100 text-slate-500'}`}>{r.batch_status || '-'}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-right font-medium text-red-600">{r.due_amount > 0 ? fmt(r.due_amount) : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ─── OPERATIONAL REPORT ──────────────────────────────────────
function OperationalReport({ initialGroupBy = '' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState(initialGroupBy || 'centre');

  const fetch = useCallback(() => {
    setLoading(true);
    api.get(`/training/reports/operational?group_by=${groupBy}`).then(r => setRows(r.data.rows || [])).catch(() => {}).finally(() => setLoading(false));
  }, [groupBy]);

  useEffect(() => { fetch(); }, [fetch]);

  const columns = groupBy === 'counsellor' ? [
    { key: 'group_name', label: 'Counsellor' },
    { key: 'student_count', label: 'Students' },
    { key: 'total_batches', label: 'Batches' },
  ] : [
    { key: 'group_name', label: groupBy === 'course' ? 'Course' : 'Centre' },
    { key: 'total_batches', label: 'Total Batches' },
    { key: 'running_batches', label: 'Running' },
    { key: 'completed_batches', label: 'Completed' },
    { key: 'on_hold_batches', label: 'On Hold' },
    { key: 'upcoming_batches', label: 'Upcoming' },
    { key: 'cancelled_batches', label: 'Cancelled' },
    ...(groupBy === 'centre' ? [{ key: 'delayed_batches', label: 'Delayed' }] : []),
    { key: 'student_count', label: 'Students' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs text-slate-500">Group By:</Label>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="operational-group-by"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="centre">Centre</SelectItem><SelectItem value="course">Course</SelectItem><SelectItem value="counsellor">Counsellor</SelectItem></SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToExcel(rows, columns, `Operational_${groupBy}_Report`)}><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToPdf(rows, columns, `Operational Report (by ${groupBy})`)}><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
      </div>
      <Card className="border p-0">
        {loading ? <div className="h-32 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div> : rows.length === 0 ? <p className="text-xs text-slate-400 text-center py-8">No data</p> : (
          <Table>
            <TableHeader><TableRow>{columns.map(c => <TableHead key={c.key} className="text-[10px] uppercase">{c.label}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i}>
                  {columns.map(c => (
                    <TableCell key={c.key} className={`text-sm ${c.key === 'group_name' ? 'font-medium' : ''} ${c.key === 'delayed_batches' && r.delayed_batches > 0 ? 'text-red-600 font-medium' : ''} ${c.key === 'cancelled_batches' && r.cancelled_batches > 0 ? 'text-red-500' : ''} ${c.key === 'running_batches' && r.running_batches > 0 ? 'text-emerald-600 font-medium' : ''}`}>
                      {r[c.key] ?? '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ─── ATTENDANCE & PLACEMENT REPORT ───────────────────────────

function MultiSelectFilter({ options, selected, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const toggle = (val) => { const s = new Set(selected); if (s.has(val)) s.delete(val); else s.add(val); onChange(Array.from(s)); };
  const label = selected.length === 0 ? placeholder : `${selected.length} selected`;
  const filtered = search ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())) : options;
  return (
    <div className="relative" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 h-8 px-3 border rounded-sm text-xs text-slate-600 bg-white hover:bg-slate-50 min-w-[120px]">
        <Filter className="w-3 h-3 text-slate-400" />{label}
        {selected.length > 0 && <X className="w-3 h-3 ml-auto text-slate-400 hover:text-red-500" onClick={e => { e.stopPropagation(); onChange([]); }} />}
      </button>
      {open && (<>
        <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearch(''); }} />
        <div className="absolute z-50 w-56 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
          <div className="p-1.5 border-b"><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full h-7 px-2 text-xs border rounded-sm outline-none focus:border-violet-300" autoFocus /></div>
          <div className="overflow-y-auto max-h-52">
            {filtered.length === 0 ? <p className="text-xs text-slate-400 text-center py-3">No matches</p> : filtered.map(o => (
              <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50 cursor-pointer text-xs"><input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} className="rounded border-slate-300" />{o.label}</label>
            ))}
          </div>
        </div>
      </>)}
    </div>
  );
}

function AttendancePlacementReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('attendance');
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [filters, setFilters] = useState({ trainer_ids: [], course_ids: [], centre_ids: [], batch_statuses: [] });

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.trainer_ids.length) params.append('trainer_id', filters.trainer_ids.join(','));
    if (filters.course_ids.length) params.append('course_id', filters.course_ids.join(','));
    if (filters.centre_ids.length) params.append('centre_id', filters.centre_ids.join(','));
    if (filters.batch_statuses.length) params.append('batch_status', filters.batch_statuses.join(','));
    api.get(`/training/reports/attendance-placement?${params}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadStudents = async (batchId) => {
    if (expandedBatch === batchId) { setExpandedBatch(null); return; }
    setExpandedBatch(batchId);
    setLoadingStudents(true);
    try {
      const r = await api.get(`/training/reports/attendance-placement/${batchId}/students`);
      setStudents(r.data.students || []);
    } catch { toast.error('Failed'); setStudents([]); }
    setLoadingStudents(false);
  };

  const hasFilters = filters.trainer_ids.length || filters.course_ids.length || filters.centre_ids.length || filters.batch_statuses.length;
  const summary = data?.summary || {};

  // Attendance view: show all batches with counts
  // Placement view: filter to batches with placement_ready_count > 0
  const displayBatches = subTab === 'placement'
    ? (data?.batches || []).filter(b => b.placement_ready_count > 0)
    : (data?.batches || []);

  // Export columns
  const batchExportCols = [
    { key: 'batch_number', label: 'Batch #' },
    { key: 'course_name', label: 'Course' },
    { key: 'trainer_name', label: 'Trainer' },
    { key: 'centre_name', label: 'Centre' },
    { key: 'batch_status', label: 'Status' },
    { key: 'student_count', label: 'Total Students' },
    { key: 'regular_count', label: 'Regular' },
    { key: 'absenteeism_count', label: 'Absenteeism' },
    { key: 'not_set_count', label: 'Not Set' },
    { key: 'placement_ready_count', label: 'Ready for Placement' },
  ];

  const studentExportCols = [
    { key: 'enrollment_no', label: 'Enrollment No' },
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'counsellor', label: 'Counsellor' },
    { key: 'attendance', label: 'Attendance' },
    { key: 'ready_for_placement', label: 'Placement Ready', render: s => s.ready_for_placement ? 'Yes' : 'No' },
    { key: 'sector', label: 'Sector' },
    { key: 'pdp', label: 'PDP' },
    { key: 'mock_interview', label: 'Mock Interview' },
  ];

  if (loading && !data) return <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;

  return (
    <div className="space-y-4" data-testid="attendance-placement-report">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <MultiSelectFilter
          options={(data?.trainers || []).map(t => ({ value: t.id, label: t.name }))}
          selected={filters.trainer_ids}
          onChange={v => setFilters(p => ({ ...p, trainer_ids: v }))}
          placeholder="All Trainers"
          testId="att-filter-trainer"
        />
        <MultiSelectFilter
          options={(data?.courses || []).map(c => ({ value: c.id, label: c.name }))}
          selected={filters.course_ids}
          onChange={v => setFilters(p => ({ ...p, course_ids: v }))}
          placeholder="All Courses"
          testId="att-filter-course"
        />
        <MultiSelectFilter
          options={(data?.centres || []).map(c => ({ value: c.id, label: c.name }))}
          selected={filters.centre_ids}
          onChange={v => setFilters(p => ({ ...p, centre_ids: v }))}
          placeholder="All Centres"
          testId="att-filter-centre"
        />
        <MultiSelectFilter
          options={[
            { value: 'Running', label: 'Running' },
            { value: 'Completed', label: 'Completed' },
            { value: 'Upcoming', label: 'Upcoming' },
            { value: 'On Hold', label: 'On Hold' },
          ]}
          selected={filters.batch_statuses}
          onChange={v => setFilters(p => ({ ...p, batch_statuses: v }))}
          placeholder="All Statuses"
          testId="att-filter-status"
        />
        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:bg-red-50" onClick={() => setFilters({ trainer_ids: [], course_ids: [], centre_ids: [], batch_statuses: [] })} data-testid="att-clear-filters">
            <X className="w-3 h-3 mr-1" />Clear Filters
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => exportToExcel(displayBatches, batchExportCols, `Attendance_Report`)} data-testid="att-export-excel">
            <FileSpreadsheet className="w-3 h-3 mr-1" />Excel
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => exportToPdf(displayBatches, batchExportCols, 'Attendance & Placement Report')} data-testid="att-export-pdf">
            <FileText className="w-3 h-3 mr-1" />PDF
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-5 gap-3">
        <Card className="border p-3 text-center"><p className="text-lg font-bold text-slate-800">{summary.total_students || 0}</p><p className="text-[10px] uppercase text-slate-400 font-semibold">Total Students</p></Card>
        <Card className="border p-3 text-center"><p className="text-lg font-bold text-emerald-600">{summary.total_regular || 0}</p><p className="text-[10px] uppercase text-slate-400 font-semibold">Regular</p></Card>
        <Card className="border p-3 text-center"><p className="text-lg font-bold text-red-600">{summary.total_absenteeism || 0}</p><p className="text-[10px] uppercase text-slate-400 font-semibold">Absenteeism</p></Card>
        <Card className="border p-3 text-center"><p className="text-lg font-bold text-slate-400">{summary.total_not_set || 0}</p><p className="text-[10px] uppercase text-slate-400 font-semibold">Not Set</p></Card>
        <Card className="border p-3 text-center"><p className="text-lg font-bold text-violet-600">{summary.total_placement_ready || 0}</p><p className="text-[10px] uppercase text-slate-400 font-semibold">Ready for Placement</p></Card>
      </div>

      {/* Sub-tabs */}
      <div className="border-b flex gap-0">
        <button onClick={() => setSubTab('attendance')} className={`px-4 py-2 text-xs font-medium border-b-2 ${subTab === 'attendance' ? 'border-cyan-500 text-cyan-700' : 'border-transparent text-slate-400'}`} data-testid="att-subtab-attendance">
          Attendance ({(data?.batches || []).length} batches)
        </button>
        <button onClick={() => setSubTab('placement')} className={`px-4 py-2 text-xs font-medium border-b-2 ${subTab === 'placement' ? 'border-violet-500 text-violet-700' : 'border-transparent text-slate-400'}`} data-testid="att-subtab-placement">
          Ready for Placement ({(data?.batches || []).filter(b => b.placement_ready_count > 0).length} batches)
        </button>
      </div>

      {/* Batch Table */}
      {displayBatches.length === 0 ? (
        <Card className="border p-12 text-center"><p className="text-xs text-slate-400">No data available</p></Card>
      ) : (
        <Card className="border p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[9px] uppercase w-8"></TableHead>
              <TableHead className="text-[9px] uppercase">Batch</TableHead>
              <TableHead className="text-[9px] uppercase">Course</TableHead>
              <TableHead className="text-[9px] uppercase">Trainer</TableHead>
              <TableHead className="text-[9px] uppercase">Centre</TableHead>
              <TableHead className="text-[9px] uppercase">Status</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Total</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Regular</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Absent</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Not Set</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Placement Ready</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {displayBatches.map(b => (<React.Fragment key={b.batch_id}>
                <TableRow key={b.batch_id} className={`cursor-pointer hover:bg-slate-50 ${expandedBatch === b.batch_id ? 'bg-violet-50/30' : ''}`}
                  onClick={() => loadStudents(b.batch_id)} data-testid={`att-batch-row-${b.batch_id}`}>
                  <TableCell>{expandedBatch === b.batch_id ? <ChevronUp className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />}</TableCell>
                  <TableCell className="text-xs font-bold text-violet-600">#{b.batch_number}</TableCell>
                  <TableCell className="text-xs">{b.course_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.trainer_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.centre_name}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[9px] ${b.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700' : b.batch_status === 'Completed' ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-blue-600'}`}>{b.batch_status}</Badge></TableCell>
                  <TableCell className="text-xs text-center font-medium">{b.student_count}</TableCell>
                  <TableCell className="text-center"><span className="text-xs font-semibold text-emerald-600">{b.regular_count}</span></TableCell>
                  <TableCell className="text-center"><span className={`text-xs font-semibold ${b.absenteeism_count > 0 ? 'text-red-600' : 'text-slate-300'}`}>{b.absenteeism_count}</span></TableCell>
                  <TableCell className="text-center"><span className="text-xs text-slate-400">{b.not_set_count}</span></TableCell>
                  <TableCell className="text-center"><span className={`text-xs font-semibold ${b.placement_ready_count > 0 ? 'text-violet-600' : 'text-slate-300'}`}>{b.placement_ready_count}</span></TableCell>
                </TableRow>
                {expandedBatch === b.batch_id && (
                  <TableRow key={`${b.batch_id}-detail`}><TableCell colSpan={11} className="p-0 bg-slate-50/50">
                    {loadingStudents ? (
                      <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-violet-500 mx-auto" /></div>
                    ) : (
                      <div className="p-3">
                        <div className="flex justify-end mb-2 gap-2">
                          <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={e => { e.stopPropagation(); exportToExcel(students, studentExportCols, `Batch_${b.batch_number}_Students`); }} data-testid={`att-batch-excel-${b.batch_id}`}>
                            <FileSpreadsheet className="w-3 h-3 mr-1" />Excel
                          </Button>
                          <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={e => { e.stopPropagation(); exportToPdf(students, studentExportCols, `Batch #${b.batch_number} - ${b.course_name}`); }} data-testid={`att-batch-pdf-${b.batch_id}`}>
                            <FileText className="w-3 h-3 mr-1" />PDF
                          </Button>
                        </div>
                        <Table>
                          <TableHeader><TableRow className="bg-white">
                            <TableHead className="text-[9px] uppercase w-6">#</TableHead>
                            <TableHead className="text-[9px] uppercase">Enrollment</TableHead>
                            <TableHead className="text-[9px] uppercase">Name</TableHead>
                            <TableHead className="text-[9px] uppercase">Phone</TableHead>
                            <TableHead className="text-[9px] uppercase">Attendance</TableHead>
                            <TableHead className="text-[9px] uppercase">Placement</TableHead>
                            <TableHead className="text-[9px] uppercase">Sector</TableHead>
                            <TableHead className="text-[9px] uppercase">PDP</TableHead>
                            <TableHead className="text-[9px] uppercase">Mock Int.</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {students.filter(s => subTab === 'placement' ? s.ready_for_placement : true).map(s => (
                              <TableRow key={s.student_id}>
                                <TableCell className="text-[10px] text-slate-400">{s.sno}</TableCell>
                                <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} /></TableCell>
                                <TableCell className="text-xs font-medium">{s.name}</TableCell>
                                <TableCell className="text-xs text-slate-500">{s.phone}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className={`text-[9px] ${s.attendance === 'Regular' ? 'bg-emerald-50 text-emerald-600' : s.attendance ? 'bg-red-50 text-red-600' : 'text-slate-300'}`}>{s.attendance || '-'}</Badge>
                                </TableCell>
                                <TableCell>{s.ready_for_placement ? <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-600">Ready</Badge> : <span className="text-[9px] text-slate-300">-</span>}</TableCell>
                                <TableCell className="text-xs text-slate-500">{s.sector || '-'}</TableCell>
                                <TableCell className="text-xs text-slate-500">{s.pdp || '-'}</TableCell>
                                <TableCell className="text-xs text-slate-500">{s.mock_interview || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TableCell></TableRow>
                )}
              </React.Fragment>))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

// ─── MAIN REPORTS PAGE ───────────────────────────────────────
// ─── Student Attendance Report ──────────────────────────────
function StudentAttendanceReport() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ rows: [], batch_summary: [], total_students: 0, total_batches: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [subTab, setSubTab] = useState('students');
  const [sortField, setSortField] = useState('attendance_pct');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    api.get('/training/batches?page_size=5000').then(r => setBatches(r.data.batches || [])).catch(() => {});
    api.get('/training/courses').then(r => setCourses(r.data.courses || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ month });
    if (batchFilter) params.set('batch_id', batchFilter);
    if (courseFilter) params.set('course', courseFilter);
    if (search) params.set('search', search);
    api.get(`/training/daily-attendance/report/student-attendance?${params}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [month, batchFilter, courseFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = [...data.rows].sort((a, b) => {
    const va = a[sortField] ?? 0, vb = b[sortField] ?? 0;
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const pctColor = (p) => p >= 80 ? 'text-emerald-600' : p >= 60 ? 'text-amber-600' : 'text-red-600';
  const pctBg = (p) => p >= 80 ? 'bg-emerald-50' : p >= 60 ? 'bg-amber-50' : 'bg-red-50';

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(sorted.map(r => ({
      'Enrollment': r.enrollment_no, 'Student': r.name, 'Course': r.course,
      'Batch #': r.batch_number, 'Batch': r.batch_name, 'Trainer': r.trainer_name,
      'Classes': r.total_classes, 'Present': r.present, 'Absent': r.absent,
      'Attendance %': r.attendance_pct, 'Best Streak': r.streak,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Student Attendance');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf]), `student_attendance_${month}.xlsx`);
  };

  const SortHeader = ({ field, label }) => (
    <TableHead className="text-[10px] uppercase cursor-pointer select-none hover:text-violet-600" onClick={() => handleSort(field)}>
      <span className="flex items-center gap-0.5">{label}
        {sortField === field ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </TableHead>
  );

  // Generate calendar dates for expanded view
  const getDatesInMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const days = new Date(y, m, 0).getDate();
    return Array.from({ length: days }, (_, i) => `${y}-${String(m).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`);
  };

  return (
    <div className="space-y-4" data-testid="student-attendance-report">
      {/* Filters */}
      <Card className="border p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <label className="text-[9px] uppercase text-slate-400 font-semibold block mb-1">Month</label>
            <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-9 text-xs w-40 rounded-sm" data-testid="att-month" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[9px] uppercase text-slate-400 font-semibold block mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300" />
              <Input placeholder="Student, enrollment, batch..." value={search} onChange={e => setSearch(e.target.value)} className="h-9 text-xs pl-8 rounded-sm" data-testid="att-search" />
            </div>
          </div>
          <div>
            <label className="text-[9px] uppercase text-slate-400 font-semibold block mb-1">Batch</label>
            <Select value={batchFilter} onValueChange={v => setBatchFilter(v === '_all' ? '' : v)}>
              <SelectTrigger className="h-9 text-xs w-44 rounded-sm" data-testid="att-batch-filter"><SelectValue placeholder="All Batches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all" className="text-xs">All Batches</SelectItem>
                {batches.filter(b => b.batch_status === 'Running').map(b => (
                  <SelectItem key={b.id} value={b.id} className="text-xs">#{b.batch_number} {b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[9px] uppercase text-slate-400 font-semibold block mb-1">Course</label>
            <Select value={courseFilter} onValueChange={v => setCourseFilter(v === '_all' ? '' : v)}>
              <SelectTrigger className="h-9 text-xs w-40 rounded-sm" data-testid="att-course-filter"><SelectValue placeholder="All Courses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all" className="text-xs">All Courses</SelectItem>
                {courses.map(c => <SelectItem key={c.id} value={c.name} className="text-xs">{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="self-end">
            <Button variant="outline" size="sm" className="h-9 text-xs rounded-sm" onClick={handleExport} disabled={!data.rows.length} data-testid="att-export">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Export
            </Button>
          </div>
        </div>
      </Card>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b">
        <button onClick={() => setSubTab('students')} className={`px-4 py-2 text-xs font-medium border-b-2 ${subTab === 'students' ? 'border-violet-500 text-violet-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`} data-testid="att-subtab-students">Students ({data.total_students})</button>
        <button onClick={() => setSubTab('batches')} className={`px-4 py-2 text-xs font-medium border-b-2 ${subTab === 'batches' ? 'border-violet-500 text-violet-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`} data-testid="att-subtab-batches">Batch Summary ({data.total_batches})</button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div>
      ) : subTab === 'students' ? (
        /* Student Table */
        <Card className="border p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[10px] uppercase">Enrollment</TableHead>
              <SortHeader field="name" label="Student" />
              <TableHead className="text-[10px] uppercase">Course</TableHead>
              <TableHead className="text-[10px] uppercase">Batch</TableHead>
              <SortHeader field="total_classes" label="Classes" />
              <SortHeader field="present" label="Present" />
              <SortHeader field="absent" label="Absent" />
              <SortHeader field="attendance_pct" label="Attendance %" />
              <SortHeader field="streak" label="Best Streak" />
            </TableRow></TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-xs text-slate-400">No attendance data for {month}</TableCell></TableRow>
              ) : sorted.map(r => (
                <React.Fragment key={r.student_id}>
                  <TableRow
                    className="cursor-pointer hover:bg-violet-50/30"
                    onClick={() => setExpandedRow(expandedRow === r.student_id ? null : r.student_id)}
                    data-testid={`att-row-${r.enrollment_no}`}
                  >
                    <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={r.enrollment_no} zohoCustomerId={r.zoho_customer_id} /></TableCell>
                    <TableCell className="text-sm font-medium">{r.name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.course}</TableCell>
                    <TableCell className="text-xs"><span className="text-violet-600 font-medium">#{r.batch_number}</span> <span className="text-slate-400">{r.batch_name}</span></TableCell>
                    <TableCell className="text-xs text-center font-medium">{r.total_classes}</TableCell>
                    <TableCell className="text-xs text-center text-emerald-600 font-medium">{r.present}</TableCell>
                    <TableCell className="text-xs text-center text-red-500 font-medium">{r.absent}</TableCell>
                    <TableCell>
                      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-bold ${pctBg(r.attendance_pct)} ${pctColor(r.attendance_pct)}`}>
                        {r.attendance_pct}%
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-center">{r.streak} days</TableCell>
                  </TableRow>
                  {expandedRow === r.student_id && (
                    <TableRow>
                      <TableCell colSpan={9} className="bg-slate-50/50 p-4">
                        <div>
                          <p className="text-[10px] uppercase text-slate-400 font-semibold mb-2">Daily Attendance — {month}</p>
                          <div className="flex flex-wrap gap-1">
                            {getDatesInMonth().map(d => {
                              const day = new Date(d).getDate();
                              const dayName = new Date(d).toLocaleDateString('en-IN', { weekday: 'short' });
                              const isWknd = [0, 6].includes(new Date(d).getDay());
                              const status = r.dates?.[d];
                              let cls = 'w-8 h-8 rounded-sm flex flex-col items-center justify-center text-[9px] border ';
                              if (status === 'present') cls += 'bg-emerald-100 border-emerald-300 text-emerald-700 font-bold';
                              else if (status === 'absent') cls += 'bg-red-100 border-red-300 text-red-700 font-bold';
                              else if (isWknd) cls += 'bg-slate-100 border-slate-200 text-slate-400';
                              else cls += 'bg-white border-slate-200 text-slate-300';
                              return (
                                <div key={d} className={cls} title={`${d} (${dayName}) — ${status || (isWknd ? 'Weekend' : 'No class')}`}>
                                  <span className="leading-none">{day}</span>
                                  <span className="leading-none text-[7px]">{status === 'present' ? 'P' : status === 'absent' ? 'A' : isWknd ? '-' : ''}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-[9px] text-slate-400">
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded-sm" /> Present</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 border border-red-300 rounded-sm" /> Absent</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-100 border border-slate-200 rounded-sm" /> Weekend</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-slate-200 rounded-sm" /> No Class</span>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        /* Batch Summary Table */
        <Card className="border p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[10px] uppercase">Batch #</TableHead>
              <TableHead className="text-[10px] uppercase">Batch Name</TableHead>
              <TableHead className="text-[10px] uppercase">Course</TableHead>
              <TableHead className="text-[10px] uppercase">Trainer</TableHead>
              <TableHead className="text-[10px] uppercase text-center">Classes Taken</TableHead>
              <TableHead className="text-[10px] uppercase text-center">Avg Attendance %</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.batch_summary.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-xs text-slate-400">No batch data for {month}</TableCell></TableRow>
              ) : data.batch_summary.map(b => (
                <TableRow key={b.batch_id} data-testid={`att-batch-${b.batch_number}`}>
                  <TableCell className="text-xs font-medium text-violet-600">#{b.batch_number}</TableCell>
                  <TableCell className="text-sm font-medium">{b.batch_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.course_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{b.trainer_name}</TableCell>
                  <TableCell className="text-xs text-center font-medium">{b.classes_taken}</TableCell>
                  <TableCell className="text-center">
                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-bold ${pctBg(b.avg_attendance_pct)} ${pctColor(b.avg_attendance_pct)}`}>
                      {b.avg_attendance_pct}%
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}


export default function ReportsPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'trainer';
  const [activeTab, setActiveTab] = useState(tabParam);
  const statusParam = searchParams.get('status') || '';
  const groupByParam = searchParams.get('group_by') || '';

  useEffect(() => {
    if (tabParam && TABS.some(t => t.id === tabParam)) setActiveTab(tabParam);
  }, [tabParam]);

  return (
    <BookmarkMapProvider>
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Reports</h1>
        <p className="text-sm text-slate-400 mt-1">Training analytics, reports & exports</p>
      </div>

      <div className="border-b border-border flex gap-0">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === tab.id ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            data-testid={`report-tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'trainer' && <TrainerReport />}
      {activeTab === 'batch' && <BatchReport initialStatus={statusParam} />}
      {activeTab === 'attendance' && <AttendancePlacementReport />}
      {activeTab === 'overdue' && <OverdueReport filterType="overdue" />}
      {activeTab === 'partially_paid' && <OverdueReport filterType="partially_paid" />}
      {activeTab === 'upgrade' && <UpgradeRevisionReport />}
      {activeTab === 'operational' && <OperationalReport initialGroupBy={groupByParam} />}
    </div>
    </BookmarkMapProvider>
  );
}
